'use client';

import React from 'react';

import { useRouter } from 'next/navigation';

import type { IconButtonProps } from '@mui/material/IconButton';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

export interface RoundIconButtonProps
    extends Omit<IconButtonProps, 'children'> {
    icon: React.ReactNode;
    color?: IconButtonProps['color'];
    component?: React.ElementType;
    href?: string;
    sx?: SxProps<Theme>;
    sizeValue?: number;
}

const StyledIconButton = styled(IconButton, {
    shouldForwardProp: prop => prop !== 'sizeValue',
})<{ sizeValue?: number }>(({ theme, sizeValue = 6 }) => ({
    borderRadius: '50%',
    width: theme.spacing(sizeValue),
    height: theme.spacing(sizeValue),
    padding: theme.spacing(4),
    boxSizing: 'border-box',
}));

const RoundIconButton: React.FC<RoundIconButtonProps> = ({
    icon,
    color = 'primary',
    sx,
    href,
    sizeValue = 6,
    ...props
}) => {
    const router = useRouter();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (href) {
            e.preventDefault();
            router.push(href);
        }

        if (props.onClick) props.onClick(e);
    };

    return (
        <StyledIconButton
            {...props}
            color={color}
            sizeValue={sizeValue}
            sx={[
                {
                    border: 1,
                    borderColor: `${color}.main`,
                    borderRadius: '50%',
                },
                ...(Array.isArray(sx) ? sx : [sx || {}]),
            ]}
            onClick={handleClick}
        >
            {icon}
        </StyledIconButton>
    );
};

export default RoundIconButton;
